import { ActionReducerMap } from '@ngrx/store';
import { AnalyticsState, analyticsStateReducer } from '@states/analytics/analytics.reducer';
import { OrganizationState, organizationStateReducer } from '@states/organization/organization.reducer';
import { SharedState, sharedStateReducer } from '@states/shared/shared.reducer';
import { InviteState, inviteStateReducer } from '@states/invite/invite.reducer';
import MenuState, { menuStateReducer } from '@states/menu/menu.reducer';
import { AlertEventsState, alertEventsStateReducer } from '@states/alert-events/alert-events.reducer';
import { SettingsState, settingsStateReducer } from '@states/settings/settings.reducer';
import { CameraEditState, cameraEditStateReducer } from '@states/camera-edit/camera-edit.reducer';
import { AlertMonitoringState, alertMonitoringStateReducer } from '@states/alert-monitoring/alert-monitoring.reducer';
import { MultiSearchState, multiSearchStateReducer } from '@states/multi-search/multi-search.reducer';
import { EdgeEditState, edgeEditStateReducer } from '@states/edge-edit/edge-edit.reducer';
import { PlaybackState, playbackStateReducer } from '@states/playback/playback.reducer';
import { ArchiveState, archiveStateReducer } from '@states/archive/archive.reducer';
import { GrantedAccessState, grantedAccessStateReducer } from '@states/granted-access/granted-access.reducer';
import { StatsState, statsStateReducer } from '@states/stats/stats.reducer';
import { UserSettingsState, userSettingsStateReducer } from '@states/user-settings/user-settings.reducer';
import { TrackObjectState, trackObjectStateReducer } from '@states/track-object/track-object.reducer';
import { TerminalState, terminalStateReducer } from '@states/terminal/terminal.reducer';
import { GeneralState, generalStateReducer } from '@states/general/general.reducer';
import { ActivityLogState, activityLogStateReducer } from '@states/activity-log/activity-log.reducer';
import { ThumbnailsState, thumbnailsStateReducer } from '@states/thumbnails/thumbnails.reducer';
import { CamerasState, camerasStateReducer } from '@states/cameras/cameras.reducer';
import { SearchState, searchStateReducer } from '@states/search/search.reducer';
import { WebRtcState, webRtcStateReducer } from '@states/webrtc/webrtc.reducer';
import { MultiPlaybackState, multiPlaybackStateReducer } from '@states/multi-playback/multi-playback.reducer';
import { PricingState, pricingStateReducer } from '@states/pricing/pricing.reducer';
import { AlertsV2State, alertsV2StateReducer } from '@states/alerts-v2/alerts-v2.reducer';
import { PeopleState, peopleStateReducer } from '@states/people/people.reducer';
import { ApiKeyState, apiKeyStateReducer } from '@states/api-key/api-key.reducer';
import { LocationEditState, locationEditStateReducer } from '@states/location-edit/location-edit.reducer';
import {
  OrganizationUsersState,
  organizationUsersStateReducer,
} from '@states/organization-users/organization-users.reducer';
import { PulsationModels } from '@models/pulsation.model';
import { storageStateReducer } from '@states/storage/storage.reducer';
import { AlertLogsState, alertLogsStateReducer } from '@states/alert-logs/alert-logs.reducer';
import { DoorsState, doorsStateReducer } from '@states/doors/doors.reducer';
import { CustomEventsState, customEventsStateReducer } from '@states/custom-events/custom-events.reducer';
import { UserGroupState, userGroupStateReducer } from '@states/user-groups/user-groups.reducer';
import {
  webRTCActiveSessionReducer,
  WebrtcActiveSessionState,
} from '@states/webrtc-active-sessions/webrtc-active-sessions.reducer';
import { VehiclesFeatureState, vehiclesStateReducer } from '@states/vehicles/vehicles.reducer';
import { OrgGroupsState, orgGroupsStateReducer } from '@states/org-groups/org-groups.reducer';
import {
  PermissionGroupState,
  permissionGroupStateReducer,
} from '@states/permission-groups/permission-groups.reducer';
import { permissionsStateReducer } from '@states/permissions/permissions.reducer';
import { DashboardState, dashboardStateReducer } from '@states/dashboard/dashboard.reducer';
import { authenticationReducer, AuthenticationState } from '@states/authentication/authentication.reducer';
import { userReducer, UserState } from '@states/user/user.reducer';
import { locationReducer, LocationState } from '@states/location/location.reducer';
import { entityLookupReducer, EntityLookupState } from '@states/entity-lookup/entity-lookup.reducer';
import { searchableReducer, SearchableState } from '@states/searchable/searchable.reducer';
import { EdgeState, locationEdgeReducer } from '@states/edge/edge.reducer';
import { CameraState, locationEdgeCameraReducer } from '@states/camera/camera.reducer';
import { CertificationState, certificationStateReducer } from '@states/certifications/certifications.reducer';
import IdentityState, { identityStateReducer } from '@states/identity/identity.reducer';
import { AppleTvState, appleTvStateReducer } from '@states/apple-tv/apple-tv.reducer';
import { UserDeviceState, userDeviceStateReducer } from '@states/device/device.reducer';
import { SupportAccessState, supportAccessStateReducer } from '@states/support-access/support-access.reducer';
import { IntegrationsState, integrationsStateReducer } from '@states/integrations/integrations.reducer';
import { UserMfaState, userMfaStateReducer } from '@states/mfa/mfa.reducer';
import { FloorState, floorStateReducer } from '@states/floors/floor.reducer';
import { HttpRequestState, httpRequestStateReducer } from '@states/http-request/http-request.reducer';
import { DCHPState, dhcpStateReducer } from '@states/dhcp/dhcp.reducer';
import { ExternalStorageState, externalStorageStateReducer } from '@states/external-storage/external-storage.reducer';
import {
  PhysicalAccessControlState,
  physicalAccessControlStateReducer,
} from '@states/physical-access-control/physical-access-control.reducer';
import { RemoteIOState, remoteIOStateReducer } from '@states/remote-io/remote-io.reducer';
import { WallV2State, wallV2StateReducer } from '@states/wall-v2/wall-v2.reducer';
import { YoutubeState, youtubeStateReducer } from '@states/youtube/youtube.reducer';
import { AccessDoorsState, accessDoorsStateReducer } from '@states/access-doors/access-doors.reducer';
import { PrivateStorageState, privateStorageStateReducer } from '@states/private-storage/private-storage.reducer';
import { CameraSettingsState, cameraSettingsStateReducer } from '@states/camera-settings/camera-settings.reducer';
import { HomeState, homeStateReducer } from '@states/home/home.reducer';
import { RouteState, routeStateReducer } from '@states/route/route.reducer';
import { AlarmState, alarmStateReducer } from '@states/alarm/alarm.reducer';
import { ScheduleState, scheduleStateReducer } from '@states/schedule/schedule.reducer';
import { syncQueueReducer, SyncQueueState } from '@states/sync-queue/sync-queue.reducer';
import { EdgeMetadataState, edgeMetadataStateReducer } from '@states/edge-metadata/edge-metadata.reducer';
import { EdgeStatusState, edgeStatusStateReducer } from '@states/edge-status/edge-status.reducer';
import { CameraStatusState, cameraStatusStateReducer } from '@states/camera-status/camera-status.reducer';
import { LocationStatusState, locationStatusStateReducer } from '@states/location-status/location-status.reducer';
import { alarmManagementReducer } from '@states/alarm-mangment/alarm-management.reducer';
import { alarmsManagerReducer, AlarmsManagerState } from '@states/alarms-manager/alarms-manager.reducer';
import { UniformsState, uniformsStateReducer } from '@states/uniforms/uniforms.reducer';
import { CustomObjectsState, customObjectsStateReducer } from '@states/custom-objects/custom-objects.reducer';
import {
  CustomObjectStatesState,
  customObjectStatesStateReducer,
} from '@states/custom-objects-states/custom-objects-states.reducer';
import { LiveKitState, liveKitStateReducer } from '@states/live-kit/live-kit.reducer';
import {
  PlaybackMonitoringState,
  playbackMonitoringStateReducer,
} from '@states/playback-monitoring/playback-monitoring.reducer';
import { UnSyncedEdgesState, unSyncedEdgesStateReducer } from '@states/unsynced-edges/unsynced-edges.reducer';
import { ArchiveStatsState, archiveStatsStateReducer } from '@states/archive-stats/archive-stats.reducer';
import { JobState, jobStateReducer } from '@states/jobs/job.reducer';
import { rateLimitReducer, RateLimitState } from '@states/rate-limit/rate-limit.reducer';
import { operatorReducer, OperatorState } from '@states/operator/operator.reducer';
import { VariablesState, variablesStateReducer } from '@states/org-variables/org-variables.reducer';
import { cameraSnapshotReducer, CameraSnapshotState } from '@states/camera-snapshot/camera-snapshot.reducer';
import { ControlEventsState, controlEventsStateReducer } from '@states/control-events/control-events.reducer';
import { sensorsReducer, SensorsState } from './states/sensors/sensors.reducer';
import { analyticsSyncReducer, AnalyticsSyncState } from './states/analytics-sync/analytics-sync.reducer';
import { OauthProviderState, oauthProviderStateReducer } from '@states/oauth-provider/oauth-provider.reducer';
import { microsoftTeamsReducer, MicrosoftTeamsState } from '@states/microsoft-teams/microsoft-teams.reducer';
import {
  AvailableDevicesState,
  availableDevicesStateReducer,
} from '@states/available-devices/available-devices.reducer';
import { IssuesState, issuesStateReducer } from '@states/issues/issues.reducer';
import { DatabaseTagsState, databaseTagsStateReducer } from '@states/database-tags/database-tags.reducer';
import { EntityTagState, entityTagStateReducer } from '@states/entity-tag/entity-tag.reducer';
import { mapCustomObjectsReducer, MapCustomObjectsState } from '@states/map-custom-objects/map-custom-objects.reducer';
import { ShelfsState, shelfsStateReducer } from '@states/shelfs/shelfs.reducer';
import { mapReducer, MapState } from '@states/map/map.reducer';
import {
  SupportOrganizationLogState,
  supportOrganizationLogStateReducer,
} from '@states/support-organization-log/support-organization-log.reducer';
import { monitoringReducer, MonitoringState } from '@states/monitoring/monitoring.reducer';
import { CertificateState, certificateStateReducer } from '@states/certificate/certificate.reducer';
import {
  OAuth2CredentialsState,
  oauth2CredentialsStateReducer,
} from '@states/oauth2-credentials/oauth2-credentials.reducer';
import { zoneRegionsStateReducer } from '@states/zone-regions/zone-regions.feature';
import { WorkflowState, workflowStateReducer } from '@states/workflow/workflow.reducer';
import { WorkflowWebhookState, workflowWebhookStateReducer } from '@states/workflow-webhooks/workflow-webhooks.reducer';
import { PersonState, personStateReducer } from '@states/person/person.feature';
import { CaseActivityLogState } from '@states/case-activity-log/case-activity-log.feature';

import StorageState = PulsationModels.StorageState;

export interface AppState {
  accessDoorsState: AccessDoorsState;
  activityLogState: ActivityLogState;
  alarmManagementState: AlarmState;
  alarmsManagerState: AlarmsManagerState;
  alarmState: AlarmState;
  alertEventsState: AlertEventsState;
  alertLogsState: AlertLogsState;
  alertMonitoringState: AlertMonitoringState;
  alertsV2State: AlertsV2State;
  analyticsState: AnalyticsState;
  analyticsSyncState: AnalyticsSyncState;
  apiKeyState: ApiKeyState;
  appleTvState: AppleTvState;
  archiveState: ArchiveState;
  archiveStatsState: ArchiveStatsState;
  authenticationState: AuthenticationState;
  cameraEditState: CameraEditState;
  cameraSettingsState: CameraSettingsState;
  cameraState: CameraState;
  cameraStatusState: CameraStatusState;
  camerasState: CamerasState;
  cameraSnapshotState: CameraSnapshotState;
  certificationState: CertificationState;
  customEventsState: CustomEventsState;
  customObjectsState: CustomObjectsState;
  customObjectStatesState: CustomObjectStatesState;
  controlEventsState: ControlEventsState;
  dashboardState: DashboardState;
  dhcpState: DCHPState;
  doorsState: DoorsState;
  edgeEditState: EdgeEditState;
  edgeMetadataState: EdgeMetadataState;
  edgeState: EdgeState;
  edgeStatusState: EdgeStatusState;
  entityTagState: EntityTagState;
  externalStorageState: ExternalStorageState;
  physicalAccessControlState: PhysicalAccessControlState;
  floorState: FloorState;
  generalState: GeneralState;
  grantedAccessState: GrantedAccessState;
  homeState: HomeState;
  identityState: IdentityState;
  integrationsState: IntegrationsState;
  inviteState: InviteState;
  jobState: JobState;
  liveKitState: LiveKitState;
  locationEditState: LocationEditState;
  locationState: LocationState;
  locationStatusState: LocationStatusState;
  entityLookupState: EntityLookupState;
  searchableState: SearchableState;
  mapCustomObjectsState: MapCustomObjectsState;
  menuState: MenuState;
  multiPlaybackState: MultiPlaybackState;
  multiSearchState: MultiSearchState;
  microsoftTeamsState: MicrosoftTeamsState;
  monitoringState: MonitoringState;
  operatorState: OperatorState;
  orgGroupsState: OrgGroupsState;
  organizationState: OrganizationState;
  organizationUsersState: OrganizationUsersState;
  peopleState: PeopleState;
  personState: PersonState;
  permissionGroupState: PermissionGroupState;
  permissionsState: PermissionState;
  playbackState: PlaybackState;
  playbackMonitoringState: PlaybackMonitoringState;
  pricingState: PricingState;
  privateStorageState: PrivateStorageState;
  remoteIOState: RemoteIOState;
  unSyncedEdgesState: UnSyncedEdgesState;
  routeState: RouteState;
  rateLimitState: RateLimitState;
  scheduleState: ScheduleState;
  searchState: SearchState;
  settingsState: SettingsState;
  sharedState: SharedState;
  shelfsState: ShelfsState;
  statsState: StatsState;
  storageState: StorageState;
  syncQueueState: SyncQueueState;
  terminalState: TerminalState;
  thumbnailsState: ThumbnailsState;
  trackObjectState: TrackObjectState;
  uniformsState: UniformsState;
  userDeviceState: UserDeviceState;
  userGroupState: UserGroupState;
  userMfaState: UserMfaState;
  userSettingsState: UserSettingsState;
  userState: UserState;
  variablesState: VariablesState;
  vehiclesState: VehiclesFeatureState;
  wallV2State: WallV2State;
  webRTCActiveSessionState: WebrtcActiveSessionState;
  webRtcSessionsState: WebRtcState;
  httpRequestState: HttpRequestState;
  youtubeState: YoutubeState;
  sensorsState: SensorsState;
  oauthProviderState: OauthProviderState;
  availableDevicesState: AvailableDevicesState;
  issuesState: IssuesState;
  databaseTagsState: DatabaseTagsState;
  mapState: MapState;
  supportOrganizationLogState: SupportOrganizationLogState;
  supportAccessState: SupportAccessState;
  certificateState: CertificateState;
  oauth2CredentialsState: OAuth2CredentialsState;
  workflowState: WorkflowState;
  WorkflowWebhookState: WorkflowWebhookState;
}

export const reducers: ActionReducerMap<any> = {
  accessDoorsState: accessDoorsStateReducer,
  activityLogState: activityLogStateReducer,
  alarmManagementState: alarmManagementReducer,
  alarmsManagerState: alarmsManagerReducer,
  alarmState: alarmStateReducer,
  alertEventsState: alertEventsStateReducer,
  alertLogsState: alertLogsStateReducer,
  alertMonitoringState: alertMonitoringStateReducer,
  alertsV2State: alertsV2StateReducer,
  analyticsState: analyticsStateReducer,
  analyticsSyncState: analyticsSyncReducer,
  apiKeyState: apiKeyStateReducer,
  appleTvState: appleTvStateReducer,
  archiveState: archiveStateReducer,
  archiveStatsState: archiveStatsStateReducer,
  authenticationState: authenticationReducer,
  cameraEditState: cameraEditStateReducer,
  cameraSettingsState: cameraSettingsStateReducer,
  cameraState: locationEdgeCameraReducer,
  cameraStatusState: cameraStatusStateReducer,
  camerasState: camerasStateReducer,
  cameraSnapshotState: cameraSnapshotReducer,
  certificationState: certificationStateReducer,
  customEventsState: customEventsStateReducer,
  customObjectsState: customObjectsStateReducer,
  customObjectStatesState: customObjectStatesStateReducer,
  controlEventsState: controlEventsStateReducer,
  dashboardState: dashboardStateReducer,
  dhcpState: dhcpStateReducer,
  doorsState: doorsStateReducer,
  edgeEditState: edgeEditStateReducer,
  edgeMetadataState: edgeMetadataStateReducer,
  edgeState: locationEdgeReducer,
  edgeStatusState: edgeStatusStateReducer,
  entityTagState: entityTagStateReducer,
  externalStorageState: externalStorageStateReducer,
  physicalAccessControlState: physicalAccessControlStateReducer,
  floorState: floorStateReducer,
  generalState: generalStateReducer,
  grantedAccessState: grantedAccessStateReducer,
  homeState: homeStateReducer,
  identityState: identityStateReducer,
  integrationsState: integrationsStateReducer,
  inviteState: inviteStateReducer,
  jobState: jobStateReducer,
  liveKitState: liveKitStateReducer,
  locationEditState: locationEditStateReducer,
  locationState: locationReducer,
  locationStatusState: locationStatusStateReducer,
  entityLookupState: entityLookupReducer,
  searchableState: searchableReducer,
  mapCustomObjectState: mapCustomObjectsReducer,
  menuState: menuStateReducer,
  multiPlaybackState: multiPlaybackStateReducer,
  multiSearchState: multiSearchStateReducer,
  microsoftTeamsState: microsoftTeamsReducer,
  monitoringState: monitoringReducer,
  operatorState: operatorReducer,
  orgGroupsState: orgGroupsStateReducer,
  organizationState: organizationStateReducer,
  organizationUsersState: organizationUsersStateReducer,
  peopleState: peopleStateReducer,
  personState: personStateReducer,
  permissionGroupState: permissionGroupStateReducer,
  permissionsState: permissionsStateReducer,
  playbackState: playbackStateReducer,
  playbackMonitoringState: playbackMonitoringStateReducer,
  pricingState: pricingStateReducer,
  privateStorageState: privateStorageStateReducer,
  remoteIOState: remoteIOStateReducer,
  unSyncedEdgesState: unSyncedEdgesStateReducer,
  routeState: routeStateReducer,
  rateLimitState: rateLimitReducer,
  scheduleState: scheduleStateReducer,
  searchState: searchStateReducer,
  settingsState: settingsStateReducer,
  sharedState: sharedStateReducer,
  shelfsState: shelfsStateReducer,
  statsState: statsStateReducer,
  storageState: storageStateReducer,
  syncQueueState: syncQueueReducer,
  terminalState: terminalStateReducer,
  thumbnailsState: thumbnailsStateReducer,
  trackObjectState: trackObjectStateReducer,
  uniformsState: uniformsStateReducer,
  userDeviceState: userDeviceStateReducer,
  userGroupState: userGroupStateReducer,
  userMfaState: userMfaStateReducer,
  userSettingsState: userSettingsStateReducer,
  userState: userReducer,
  variablesState: variablesStateReducer,
  vehiclesStateReducer: vehiclesStateReducer,
  wallV2State: wallV2StateReducer,
  webRTCActiveSessionState: webRTCActiveSessionReducer,
  webRtcSessionsState: webRtcStateReducer,
  httpRequestState: httpRequestStateReducer,
  youtubeState: youtubeStateReducer,
  sensorsState: sensorsReducer,
  oauthProviderState: oauthProviderStateReducer,
  availableDevicesState: availableDevicesStateReducer,
  issuesState: issuesStateReducer,
  databaseTagsState: databaseTagsStateReducer,
  mapState: mapReducer,
  supportOrganizationLogState: supportOrganizationLogStateReducer,
  supportAccessState: supportAccessStateReducer,
  certificateState: certificateStateReducer,
  oauth2CredentialsState: oauth2CredentialsStateReducer,
  zoneRegionsState: zoneRegionsStateReducer,
  workflowState: workflowStateReducer,
  workflowWebhookState: workflowWebhookStateReducer,
};
